/**
 * Fast Simon — conversion-funnel events (add to cart, cart visited, direct product view).
 *
 * This asset closes the analytics loop the results and recommendations controllers open:
 * they report WHAT shoppers saw and clicked; this reports what they DID with it — adding to
 * cart, landing on the cart, and viewing a product directly (organic/direct traffic) rather
 * than through a Fast Simon search or collection. Without these, impressions and clicks record
 * but the conversion / revenue side of the Fast Simon admin stays empty.
 *
 * Unlike the results/recommendations controllers (loaded per-surface), this is loaded globally
 * in layout/theme.liquid: an add-to-cart can happen on the product page AND on any PLP via the
 * quick-add modal, the cart page needs CartVisited, and a direct product view can happen on any
 * product page. It carries no UI of its own — it only listens for the theme's own events and
 * forwards them to the Fast Simon SDK.
 *
 * Page context (isCart, productId, pageType) is published by layout/theme.liquid as
 * `window.__fastSimonPageContext` before this script loads.
 *
 * Events (all documented Fast Simon analytics events):
 *  - `AddToCartPerformed`   on a genuine add-to-cart, carrying the product id. Fired from the
 *                           theme's `cart:update` event, filtered to product-form adds only
 *                           (see below) so cart-line quantity edits are not mistaken for adds.
 *  - `CartVisited`          once when the cart page loads.
 *  - `DirectProductClicked` on a product-page load from a genuinely direct source (an external
 *                           referrer, or none) — never for same-origin navigations, which may be
 *                           Fast Simon autocomplete-driven (see isDirectProductView).
 *
 * Every event goes through `fsEvent`, which resolves the event name against the enum the loaded
 * SDK build actually exposes — the same pattern as fs-sdk-results.js / fs-sdk-recommendations.js,
 * because this store's build has lagged the documented names before.
 *
 * reportAppType: the Fast Simon cart documentation specifies `reportAppType: "SPA"` for the cart
 * events (an add-to-cart here is an AJAX action with no navigation, and the cart drawer opens in
 * place), so it is passed exactly as documented. `DirectProductClicked` takes none per the docs.
 *
 * Performance: no scroll listeners, no intervals for the listeners themselves; the add-to-cart
 * listener is a single delegated `cart:update` handler, and the one-time page-load events wait on
 * the SDK-readiness poll the other controllers already use.
 */
(() => {
  'use strict';

  if (window.__fsCartControllerLoaded) return;
  window.__fsCartControllerLoaded = true;

  /**
   * fsEvent submission outcomes, distinguished so each failure mode gets the correct policy:
   *  - `SENT`      submitted without throwing.
   *  - `THREW`     the SDK call threw. In MPA mode reports are enqueued and flushed on the next
   *                initialization, so delivery is UNKNOWN — never retry (duplicate risk).
   *  - `NOT_READY` NOT attempted because the SDK isn't ready (onReady hasn't fired / event method
   *                missing). This is TEMPORARY — safe and worthwhile to retry once ready.
   *  - `UNSUPPORTED` the SDK is ready and the event method exists, but this build's enum does not
   *                expose the event name. This is PERMANENT — retrying the same name can never
   *                succeed, so the caller must treat it as terminal and settle its guard/queue.
   * Separating NOT_READY from UNSUPPORTED is what keeps "retryable" promises honest: only NOT_READY
   * is ever retried.
   */
  const SENT = 'sent';
  const THREW = 'threw';
  const NOT_READY = 'not-ready';
  const UNSUPPORTED = 'unsupported-event';

  /**
   * True when the SDK's onReady (initialization) has completed — set by our wrapped onReady in the
   * bootstrap, and by the shared SDK config in theme.liquid. This is what makes a send SAFE from a
   * timing standpoint: per the SDK's reporting contract, in MPA mode (this store) reports are only
   * flushed on the next initialization, so sending before onReady can happen prematurely and be
   * lost. Event-surface presence alone is not readiness — see sdkReady().
   *
   * @returns {boolean}
   */
  const sdkInitialized = () => window.__fastSimonSdkReady === true;

  /**
   * True when the SDK's event surface is present — the enum and the event method exist. A NECESSARY
   * but not SUFFICIENT condition for readiness: presence can precede onReady, which is why the
   * initialized() check is layered on top in sdkReady().
   *
   * @returns {boolean}
   */
  const sdkEventUsable = () =>
    !!window.FastSimonEventName && typeof window.FastSimonSDK?.event === 'function';

  /**
   * True only when the SDK is genuinely READY to accept reports. Two things must both hold:
   *  - `sdkInitialized()` — onReady has fired, so a send won't be premature.
   *  - `sdkEventUsable()` — the event surface exists, so a build that set the ready flag but never
   *    attached `FastSimonEventName` cannot slip a no-op send past us.
   * Requiring BOTH is what makes the readiness check sound in either direction.
   *
   * @returns {boolean}
   */
  const sdkReady = () => sdkInitialized() && sdkEventUsable();

  /**
   * Reports an event by the first name the loaded SDK build actually exposes, with an optional
   * per-event reportAppType (used for the cart events, per the Fast Simon cart docs).
   *
   * Returns a status so callers can apply the correct retry policy: only `NOT_READY` is retried;
   * `SENT` and `THREW` are done (THREW's delivery is unknown but retrying risks a duplicate), and
   * `UNSUPPORTED` is a permanent miss that must settle the caller's guard/queue. A successful return
   * is "submitted without throwing", NOT confirmed network delivery — the actual flush happens at
   * the next initialization and is not observable here.
   *
   * @param {string|string[]} name one event name, or names in order of preference
   * @param {object} data
   * @param {string} [reportAppType] optional AppType override ("SPA" | "MPA")
   * @returns {string} one of SENT | THREW | NOT_READY | UNSUPPORTED
   */
  const fsEvent = (name, data, reportAppType) => {
    const names = window.FastSimonEventName;
    if (!sdkInitialized()) return NOT_READY; // not initialized yet — temporary, retry later
    if (!names || typeof window.FastSimonSDK?.event !== 'function') return NOT_READY;
    // `!= null` accepts a valid falsy value like 0 while rejecting a missing key, null or
    // undefined — the correct existence test for an enum whose values are strings.
    const candidate = (Array.isArray(name) ? name : [name]).find((n) => names[n] != null);
    if (!candidate) return UNSUPPORTED; // initialized, but this build has no such event name

    // SAFETY: a stale/older SDK build can expose a non-string value (e.g. a PointerEvent
    // function/object) where a string enum value was expected. Passing that directly as
    // `eventName` produces a malformed report that Fast Simon silently discards, leaving
    // the conversion funnel empty. Fall back to the enum key (`candidate`, always a
    // string) so the event is still reported rather than dropped.
    const _eventName = names[candidate];
    const eventName = typeof _eventName === 'string' ? _eventName : candidate;
    
    try {
      const report = { eventName, data };
      if (reportAppType) report.reportAppType = reportAppType;
      // Verified against the SDK contract: event()/saveEvent()/reportEvent() are typed `void` —
      // synchronous, fire-and-forget, no Promise. In MPA mode the report is enqueued and flushed
      // on the next initialization, so a return here is NOT confirmed network delivery, and a throw
      // leaves the outcome unknown (it may already be enqueued). Both facts are why the outcome is
      // a multi-valued status rather than a boolean.
      window.FastSimonSDK.event(report);
      return SENT;
    } catch (error) {
      console.warn('[Fast Simon] event failed', candidate, error);
      return THREW;
    }
  };

  /* ------------------------------------------------------------------- add to cart */

  /**
   * Add-to-cart events that fire before the SDK is ready cannot be reported yet — `fsEvent`
   * silently returns when the SDK or its enum is missing. Rather than drop them, genuine additions
   * are queued here and flushed the moment the SDK becomes ready (see flushPendingAdds). A shopper
   * who adds within the readiness gap (fast machines, warm cache) still gets counted.
   * @type {Array<{productID: string}>}
   */
  const pendingAdds = [];

  /**
   * Sends every queued add-to-cart now that the SDK is ready. An item is dropped from the queue
   * unless its submission returned NOT_READY — i.e. the SDK still wasn't ready, so it is genuinely
   * retryable. A THREW item must NOT be retried (per the SDK's MPA enqueue-then-flush contract the
   * throw leaves delivery unknown; retrying risks a duplicate), and an UNSUPPORTED item can never
   * succeed on retry, so both are dropped rather than left to retry forever.
   */
  const flushPendingAdds = () => {
    if (!pendingAdds.length) return;
    const remaining = [];
    for (const item of pendingAdds) {
      if (fsEvent('AddToCartPerformed', item, 'SPA') === NOT_READY) remaining.push(item);
    }
    pendingAdds.length = 0;
    pendingAdds.push(...remaining);
  };

  /**
   * A real add-to-cart is a `cart:update` whose source is the product form (the PDP buy button or
   * a quick-add modal, both of which submit a `product-form-component`). Cart-line quantity edits
   * dispatch the same event name with `source: 'cart-items-component'`, and failed adds set
   * `didError`; both are excluded here so only genuine additions reach Fast Simon.
   */
  document.addEventListener('cart:update', (event) => {
    const data = event.detail && event.detail.data;
    if (!data || data.didError) return;
    if (data.source !== 'product-form-component') return;
    const productID = data.productId;
    if (!productID) return;
    const payload = { productID: String(productID), cartToken: window.__fastSimonPageContext?.cartToken || null };
    // Submit now when the SDK is ready. On any terminal outcome (SENT, THREW — neither retried —
    // or UNSUPPORTED, which can never succeed) this add is handled. Only a SENT actually proves the
    // SDK accepts events, so on SENT we also flush any older items queued during an earlier gap so
    // they are not left stranded. Only NOT_READY (SDK still not usable) queues this one for the
    // readiness flush.
    if (sdkReady()) {
      const status = fsEvent('AddToCartPerformed', payload, 'SPA');
      if (status !== NOT_READY) {
        if (status === SENT) flushPendingAdds();
        return;
      }
    }
    pendingAdds.push(payload);
  });

  /* ------------------------------------------------------- one-time page-load events */

  const ctx = window.__fastSimonPageContext || {};

  /**
   * True only when the product-page view is PRESUMED direct — i.e. Fast Simon most likely did not
   * drive the shopper here. That is the case when there is no referrer (bookmark / typed URL /
   * stripped) or the referrer is a DIFFERENT origin (Google, social, email, an affiliate…). This is
   * a heuristic, not proof — see the known-limits note below.
   *
   * A same-origin referrer is deliberately treated as NOT direct. It could be a Fast Simon
   * surface — the SDK-driven /search or /collections listing, a recommendation widget, OR the
   * header autocomplete. The autocomplete is Fast Simon's own V-Next widget, which reports its own
   * AutocompleteProductClicked; a suggestion click lands on the PDP with a same-origin referrer
   * (e.g. the page the search box was on). Counting that here as well would double-count the view
   * and mislabel an autocomplete-driven visit as organic/direct. Since we cannot reliably tell a
   * V-Next autocomplete navigation apart from any other same-origin navigation, the safe rule is:
   * only fire DirectProductClicked when Fast Simon most likely did not send the shopper (no
   * referrer, or a foreign origin).
   *
   * Known limits of this heuristic (deliberate, not a defect): it can neither prove nor disprove
   * attribution perfectly. An empty referrer can also come from a same-origin link carrying
   * `rel=noreferrer` or a restrictive Referrer-Policy, which would be miscounted as direct; and a
   * same-origin navigation that Fast Simon did NOT drive (e.g. a plain product link in site
   * content) is excluded and so is undercounted. There is no positive "came from Fast Simon"
   * marker on this integration — the listings and the V-Next autocomplete both navigate with an
   * ordinary product URL — so referrer origin is the best signal available, and it is biased toward
   * undercounting direct rather than double-counting a Fast Simon-driven view.
   *
   * @returns {boolean}
   */
  const isDirectProductView = () => {
    const ref = document.referrer;
    if (!ref) return true; // bookmark / typed URL / stripped referrer — presumed direct
    let url;
    try {
      url = new URL(ref);
    } catch (error) {
      return true; // unparseable referrer — presume direct rather than guess
    }
    return url.origin !== window.location.origin; // foreign origin = presumed direct; same-origin = not
  };

  // One-time guard: the wrapped onReady AND the readiness poll can both observe readiness (the
  // poll ticks up to 500ms after onReady has already fired), so without this the page-load events
  // would fire twice. This is the same idempotency guard the recommendations controller uses. The
  // flag is only set once an event reached a TERMINAL state — SENT, THREW (must not be retried), or
  // UNSUPPORTED (permanent — retrying the same missing enum name can never succeed) — OR once
  // neither page event is applicable on this page. A NOT_READY outcome deliberately does NOT set
  // the flag, leaving the events retryable rather than silently dropped.
  let pageEventsFired = false;
  const firePageEvents = () => {
    if (pageEventsFired) return;
    if (!sdkReady()) return; // not ready — leave for a later flush
    let terminal = false; // an event reached SENT / THREW / UNSUPPORTED — never retry
    // NOTE: a NOT_READY outcome deliberately does NOT set the flag. Because we only get here when
    // sdkReady() is true it is in practice unreachable, but if it ever occurred it must leave the
    // guard unsettled so the poll / a later flush can still deliver the events.
    // Compute product applicability ONCE so the same value drives both the send and the guard —
    // a same-origin product view (not direct) has nothing to report and must still settle the guard.
    const shouldReportProduct =
      ctx.pageType === 'product' && !!ctx.productId && isDirectProductView();
    if (ctx.isCart) {
      if (fsEvent('CartVisited', {}, 'SPA') !== NOT_READY) terminal = true;
    }
    if (shouldReportProduct) {
      if (fsEvent('DirectProductClicked', { productID: String(ctx.productId) }) !== NOT_READY) {
        terminal = true;
      }
    }
    // Settle the guard once an event reached a terminal state, or once neither page event applies
    // (not a cart, and not a direct product view) — so any page with nothing left to report settles
    // immediately instead of retrying forever. A NOT_READY leaves it unsettled so the events can
    // still be delivered on a later flush.
    if (terminal || (!ctx.isCart && !shouldReportProduct)) pageEventsFired = true;
  };

  /* ------------------------------------------------------------------------ bootstrap */

  // Readiness contract: the SDK only accepts reports from its onReady (initialization finished)
  // onward — in this store's MPA mode reports are only flushed on the next initialization, so a
  // premature send can be lost. Readiness is therefore sdkReady() = __fastSimonSdkReady (onReady
  // fired) AND sdkEventUsable() (the event surface exists). Both the immediate check, the wrapped
  // onReady and the poll gate on that same combined condition, so the poll is a genuine safety net:
  // it keeps trying until the SDK is truly ready. Intentional best-effort limit: the poll gives up
  // after ~8s, so readiness that arrives later than that (after the wrapped onReady AND the poll
  // have both run) can still strand the queued adds and page-load events — this is a deliberate
  // cap, not a guarantee against a stale flag or a premature send within the window.
  let pollTimer = null;
  const onSdkReady = () => {
    if (pollTimer !== null) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    flushPendingAdds();
    firePageEvents();
  };

  if (sdkReady()) {
    onSdkReady();
  } else {
    const config = window.__fast_simon_sdk_config;
    if (config && typeof config.onReady === 'function' && !config.__fsCartHooked) {
      config.__fsCartHooked = true;
      const onReady = config.onReady;
      config.onReady = function (...args) {
        try {
          if (typeof onReady === 'function') onReady.apply(this, args);
        } finally {
          window.__fastSimonSdkReady = true;
          // Act only if the SDK is genuinely ready; otherwise leave it to the poll, which gates on
          // sdkReady() and cannot be stranded by this flag.
          if (sdkReady()) onSdkReady();
        }
      };
    }

    let tries = 0;
    const poll = () => {
      if (sdkReady()) return onSdkReady();
      if (tries >= 16) {
        // SDK never became ready (blocked CDN, removed config, or a build that never attached
        // FastSimonEventName). The page-load funnel events are dropped and any queued adds stay
        // queued (they would only fail again); warn so this is visible.
        console.warn('[Fast Simon] SDK not ready in time; cart-visited / direct-view events dropped');
        return;
      }
      tries += 1;
      pollTimer = setTimeout(poll, 500);
    };
    poll();
  }
})();

